import template from './datasets-numeric-filter.hbs'
import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import NumericFilter from 'scoreboard/components/numeric-filter'
import formHelper from 'scoreboard/components/form-helper'
import rangeSelectMultiple from './range-select-multiple'
import { renderValueOrEquations } from './value-or-equation'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import highlight from 'scoreboard/components/highlight'
import Dataset from 'scoreboard/datasets/model/dataset'
import config from 'scoreboard/components/config'
import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT } = DataType

var DatasetsNumericFilter = NumericFilter.extend({

	/** @type {Dataset} */
	model: null,

	/**
	 * Extra controls that may appear for number/currency/percent filters in
	 * filter-step1, depending on the operator (aka filter type)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the dataset
	 *
	 * @param {Object} options.filter the filter we're working with
	 *
	 * @param {Object} options.field the field being filtered on
	 *
	 * @param {Boolean} options.new indicates if this is a new filter or not
	 *
	 * @param {FormView} options.formView the FormView
	 *
	 * @param {String[]} options.equations any equations specified for
	 * "is any" or "is not any" filters
	 */
	initialize(options) {
		this.options = options
		NumericFilter.prototype.initialize.apply(this, arguments)
		this.formView = this.options.formView
		var fieldIdentifier = this.$('[name=fieldIdentifier]').val()
		// #40586 prevent blowup editing transformations for value imports
		if (this.model instanceof Dataset) {
			this.field =  this.model && this.model.getFieldByFieldIdentifier(fieldIdentifier)
		}
		this.equations = this.options.equations
	},

	render: function() {
		this.$('.datasets-numeric-filter').empty().append(template())

		// don't write to this.el because we don't want to clobber the fieldId
		// filteringSelect
		formHelper.writeToDom(this.options.filter, this.$('.datasets-numeric-filter'))

		if (_.get(this.field, 'ranges')) {
			rangeSelectMultiple(this.$('[name=rangeIds]'), this.field.ranges,
				this.options.filter.rangeIds)
		}

		let dataType = this.options.dataType
		let movePercentPlacesBy = undefined
		if (!!this.options.isScorecardScore && !!config.showScoresAsPercent) {
			dataType = PERCENT
			movePercentPlacesBy = !!config.showScoresAsPercent ? 2 : 1
		}

		// after formHelper.writeToDom
		this.valueOrEquations = renderValueOrEquations({
			model: this.model,
			filter: this.options.filter,
			formView: this.formView,
			field: this.field,
			el: this.$el,
			dataType,
			validator: this.options.validator,
			movePercentPlacesBy,
			currencyCode: this.options?.field?.currencyCode
		})

		this.finishRendering()

		return this
	},

	_applyOperator: function(e) {
		NumericFilter.prototype._applyOperator.apply(this, arguments)
		var operator = this._val()

		// toggle div.first-value as appropriate
		var noFirstValue =
			operator === 'is-blank' || operator === 'not-blank' ||
			operator === 'in-the-range' || operator === 'not-in-any-range' ||
			operator === 'is-any' || operator === 'is-not-any' ||
			operator === 'character-length-is-any' ||
			operator === 'character-length-is-not-any' ||
			operator === 'is-number' || operator === 'is-not-number'

		this.$('.first-value').toggleClass('is-not-displayed', noFirstValue)

		this.$('.between-number').toggleClass('is-not-displayed', !this._isBetween())

		// toggle select.in-the-range as appropriate
		var hideRangeControls = operator !== 'in-the-range'
		this.$('.in-the-range').toggleClass('is-not-displayed', hideRangeControls)

		// toggle the entire .datasets-numeric-filter div as appropriate
		this.$('.datasets-numeric-filter').toggleClass('is-not-displayed', noFirstValue && hideRangeControls)

		let anyOperator = operator === 'is-any' || operator === 'is-not-any' ||
			operator === 'character-length-is-any' ||
			operator === 'character-length-is-not-any'
		this.$('.add-equation').toggleClass('is-not-displayed', !anyOperator)

		// toggle the "values" control based on whether we're showing the is-any
		// or is-not-any operator
		this.$('[name=values], .select.control.is-any').toggleClass('is-not-displayed', !anyOperator)
		this.trigger('change-values-operator', 'is-any', 'is-not-any',
			'character-length-is-any', 'character-length-is-not-any')

		_.invokeMap(this.valueOrEquations, 'changeUpdateType')
	},

	_isBetween: function() {
		return this._val() === 'between-number' ||
			this._val() === 'character-length-between'
	},

	/**
	 * Callback that should be invoked (by filter-step1) when an equation changes
	 * @param oldEquation the equation before it was changed
	 * @param equation the equation after it was changed
	 * @param equations all the equations specified for "is any" or "is not any"
	 * filters
	 */
	onEquationChanged: function(oldEquation, equation, equations) {
		this.equations = equations
	},

	test: function() {
		if (this._val() === 'in-the-range' &&
			_.isEmpty(this.$('[name=rangeIds]').val())) {
			return i18n.get('datasets.fields.ranges.required')
		}

		// #26085 make sure value is specified for the values select
		let isValuesOperator = this._val() === 'is-any' || this._val() === 'is-not-any'
		if (isValuesOperator) {
			let values = this.$('[name=values]').val() || []
			if (_.isEmpty(values) && !this.options.allowIncomplete) {
				return i18n.get('datasets.filters.value.required')
			}

			_.pull(values, ...this.equations)
			for (let i=0; i<values.length; i++) {
				if (numericOrBlankValidator(values[i])) {
					let highlighted = highlight(this.options.field.label)
					return i18n.get('datasets.filters.value.number', highlighted)
				}
			}
		}

		// #26363 only test valuesOrEquations for operators that display it
		let isValuesOrEquationOperator = _.includes(['between-date-time', 'greater-than',
			'greater-than-or-equals', 'less-than', 'less-than-or-equals'],
			this._val())
		if (isValuesOrEquationOperator) {
			return _.first(_.compact(_.invokeMap(this.valueOrEquations, 'test')))
		}
	},

	saveState: function() {
		var numericOptions = formHelper.readFromDom(this.$('.datasets-numeric-filter'))
		_.extend(this.options.filter, numericOptions)
		if (this._val() === 'in-the-range') {
			this.options.filter.rangeIds = this.$('[name=rangeIds]').val()
		}
		if (_.isNil(this.options.filter.aggregationType)) {
			var field = this.model.getFieldByIds(this.options.filter)
			this.options.filter.aggregationType = field.aggregationType
		}
	},

	// similar to this.remove, but only removes the stuff inside
	// .datasets-numeric-filter rather than the whole element
	cleanup: function() {
		this.$('.datasets-numeric-filter').empty()
		this.undelegateEvents()
		_.invokeMap(this.valueOrEquations, 'cleanup')
	},

	remove() {
		NumericFilter.prototype.remove.apply(this, arguments)
		this.cleanup()
		_.invokeMap(this.valueOrEquations, 'remove')
	}

})

function numericOrBlankValidator (val) {
	if (val === Dataset.BLANK) {
		return
	}
	return numericValidator(val)
}

export default DatasetsNumericFilter

DatasetsNumericFilter.__filename__ = 'datasets/filters/datasets-numeric-filter.js'
